<!--
    This component will manage the search in the navbar :
    - display a search icon
    - display a responsive dialog

    <NavBarSearch />
 -->
<script setup lang="ts">
/* UTILS */
import { AfIconWithHover, AfTextfield } from '@af-lib/ui-lib';
import { ALButton, ALLoader, ALIconButton, useCssBreakpoints, ALDivider } from '@af-lib/vue-ui';
import { useDebounceFn } from '@vueuse/core';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/* HELPERS */
import { useGetSearchUsers } from '@/api/vue-query/users/users.queries';
/* COMPONENTS */
import SearchProfileCard from '@/components/profileCard/SearchProfileCard.vue';
import Text from '@/components/text/Text.vue';
/* STORES */
import { useAuthStore } from '@/stores/auth';

const MIN_CHARS_SEARCH = 3;

const { isMobile, isTablet, isDesktop, isLargeDesktop, tabletAndDown } = useCssBreakpoints();

const dialogWidth = computed(() => {
  if (isLargeDesktop.value) return '1240px';
  if (isDesktop.value) return '60%';
  if (isTablet.value) return '80%';
  return '100%';
});
const dialogHeight = computed(() => (isMobile.value ? '100%' : '80%'));
const router = useRouter();
const route = useRoute();
const hasInvitePermission = useAuthStore().hasPermission('invite_cta');

// Results list
const isDialogOpen = ref(false);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const searchInput = ref<any>(null);
const searchTerm = ref('');

const usersList = computed(() => searchedUsersResponse.value?.body);
const isSearchEnabled = computed(() => searchTerm.value?.length >= MIN_CHARS_SEARCH);
const hasReachedContactInvitationLimit = useAuthStore().hasReachedContactInvitationLimit;

const { data: searchedUsersResponse, isFetching: isLoading } = useGetSearchUsers(searchTerm, isSearchEnabled);

/* FUNCTIONS */
/**
 * On input (or on clear) will launch a search, or display initial state
 */
let focusTimeout: ReturnType<typeof setTimeout>;

const focusInput = () => {
  focusTimeout = setTimeout(() => {
    const inputElement = searchInput.value?.$el?.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }, 100);
};

onBeforeUnmount(() => {
  clearTimeout(focusTimeout);
});

const onInputSearch = useDebounceFn(search => {
  if (search.isError) {
    return;
  }

  searchTerm.value = search.trim();
}, 500);
/**
 * Reset data before closing.
 * Real close is done by updating the modelvalue in the template
 */

function redirectToInvite() {
  router.push({ name: 'network-invite' });
  isDialogOpen.value = false;
}

/* WATCH */
// Reset modal and its search values when route change or when the modal is closed
watch(route, () => (isDialogOpen.value = false));

watch(isDialogOpen, () => {
  if (isDialogOpen.value) focusInput();
});
</script>

<template>
  <v-dialog v-model="isDialogOpen" :fullscreen="isMobile" :width="dialogWidth" :height="dialogHeight" scrollable>
    <template #activator="{ props }">
      <div v-bind="props">
        <ALIconButton
          icon="fa fa-search"
          variant="tertiary"
          :label="$t('accessibility.search.cta')"
          :shouldTeleportTooltip="true"
        />
      </div>
    </template>

    <template #default="{ isActive }">
      <v-card class="h-100">
        <v-card-title class="d-flex flex-column px-3 py-6 pa-md-5">
          <div :class="['w-100 d-flex', tabletAndDown ? 'align-center justify-space-between' : 'align-start']">
            <div class="grow">
              <AfTextfield
                ref="searchInput"
                :aria-label="$t('general.search')"
                :placeholder="$t('general.search')"
                :subtext="$t('recruiter_network.search.requirement')"
                fieldType="open"
                icon="fat fa-search"
                @updateInput="onInputSearch"
              />
            </div>
            <AfIconWithHover
              v-if="!tabletAndDown"
              :aria-label="$t('general.close')"
              role="button"
              class="text-subtitle-1 ms-6"
              icon="fa-xmark-large"
              @click="isDialogOpen = false"
            />
            <ALButton
              v-else
              type="tertiary"
              class="ms-6 align-self-start"
              size="s"
              :label="$t('general.cancel')"
              :aria-label="$t('general.close')"
              @click="isActive.value = false"
            />
          </div>
        </v-card-title>

        <v-card-text class="position-relative overflow-x-hidden py-6 px-0 py-md-3">
          <!-- loading state -->
          <div v-if="isLoading" class="d-flex flex-column align-center justify-center h-100 px-3 px-md-5">
            <ALLoader size="xl" />
          </div>
          <template v-else>
            <!-- initial state -->
            <div
              v-if="!isSearchEnabled"
              class="initial-state position-absolute d-flex flex-column align-center justify-center px-3 px-md-5"
            >
              <div class="gradients-container position-absolute"></div>
              <div>
                <v-img class="mb-5" :src="$staticAssets('semi-unicorn.png')" width="226" height="122" />
              </div>
              <h3 class="text-h3 text-primary">{{ $t('recruiter_network.search.title') }}</h3>
              <Text type="body3" class="mt-3">{{ $t('recruiter_network.search.body') }}</Text>
            </div>

            <!-- no results -->
            <div
              v-else-if="!usersList?.length"
              class="h-100 d-flex flex-column align-center justify-center px-3 px-md-5"
            >
              <div>
                <v-img class="mb-5" :src="$staticAssets('gradients-bubble-user.svg')" width="88" />
              </div>
              <Text type="body3" class="mb-6">{{ $t('general.error.oops') }}</Text>
              <ALButton
                v-if="hasInvitePermission"
                type="secondary"
                :label="$t('general.invite.contacts')"
                :tooltipLabel="hasReachedContactInvitationLimit ? $t('recruiter_network.invite.limit.tooltip') : ''"
                :isDisabled="hasReachedContactInvitationLimit"
                @click="redirectToInvite"
              ></ALButton>
            </div>

            <!-- results -->
            <template v-else>
              <div v-for="user in usersList" :key="user.user_uuid">
                <SearchProfileCard :user="user" class="px-3 px-md-5" />
                <ALDivider />
              </div>
            </template>
          </template>
        </v-card-text>
      </v-card>
    </template>
  </v-dialog>
</template>

<style scoped lang="scss">
.initial-state {
  height: 300px;
  left: 0;
  right: 0;
  bottom: 50%;
  transform: translateY(50%);
}

.gradients-container {
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    90deg,
    rgba(0, 150, 255, 0.07) -0.04%,
    rgba(2, 156, 175, 0.07) 21.84%,
    rgba(245, 159, 0, 0.07) 46.85%,
    rgba(240, 111, 56, 0.07) 71.44%,
    rgba(255, 47, 146, 0.07) 100%
  );
  filter: blur(50px);
  transform: rotate(12.34deg);
}
</style>
